/**
 * หน้าจอความล้มเหลวของกลุ่ม RBAC Admin — หนึ่งกฎ หนึ่งข้อความ
 *
 * ทุกกรณีที่ `docs/API_CONTRACT.md` นิยามไว้ต้องอ่านออกว่าเกิดอะไรและ **ใครเป็น
 * ผู้แก้** ห้ามยุบเป็น "บันทึกไม่สำเร็จ" ก้อนเดียว และในกลุ่มนี้มีข้อที่สำคัญ
 * เป็นพิเศษอยู่หลายข้อ:
 *
 * - `LAST_ADMIN_CANNOT_BE_REMOVED` — **การ์ดที่กันผู้ดูแลไม่ให้ล็อกทุกคนออกจาก
 *   ระบบของตัวเอง** ถอด `ADMIN` จากคนสุดท้ายที่ยัง `ACTIVE` ไม่ได้ เพราะระบบที่
 *   ไม่เหลือ ADMIN คือระบบที่ไม่มีใครแก้สิทธิ์ได้อีกและไม่มีทางกู้ผ่าน UI ข้อความ
 *   ของมันต้องบอก **ทางออก** ด้วย (มอบ ADMIN ให้คนอื่นก่อน) ไม่ใช่แค่บอกว่าไม่ได้
 * - `ROLE_CODE_NOT_RECOGNISED` — คนละเรื่องกันสิ้นเชิงแม้จะเป็น `422` เหมือนกัน:
 *   รหัสบทบาทที่ส่งไปไม่อยู่ในชุดที่เซิร์ฟเวอร์รับ ซึ่งแปลว่าตัวเลือกบนหน้าจอกับ
 *   `SYSTEM_ROLE_CODES` ฝั่ง backend เลื่อนออกจากกันแล้ว
 * - `SYSTEM_ADMIN_MODULE_NOT_GRANTABLE` — **ไม่ควรเกิดจากตารางสิทธิ์เลย** เพราะช่อง
 *   ติ๊กของ module กลุ่มนั้นถูกปิดและไม่มีเส้นทางประกอบ `POST` ให้มัน การที่มันเกิด
 *   ขึ้นจริงแปลว่าหน้าจอกับ API ไม่ตรงกันแล้ว (เช่นแคตาล็อกฝั่ง backend เพิ่ม module
 *   เข้ากลุ่มนี้แต่ธง `is_system_admin_module` ยังไม่ตามมา) — เป็นบั๊กที่ต้องเห็น
 *   ไม่ใช่ความล้มเหลวที่กลืนเงียบ ๆ ให้ดูเหมือนบันทึกสำเร็จ
 * - `MODULE_NOT_FOUND` — รหัส module ที่ส่งไปไม่อยู่ในแคตาล็อกแล้ว ซึ่งแปลว่า grid
 *   ที่เปิดค้างอยู่เก่าไปแล้ว ทางแก้คือโหลดใหม่
 *
 * - `DEPARTMENT_NAME_TAKEN` / `EMPLOYEE_ID_TAKEN` — **`409` ไม่ใช่ `422`**: ไม่ใช่กฎ
 *   ทางธุรกิจที่ต้องแก้เงื่อนไขก่อน แต่คือ "ของสิ่งนี้มีเจ้าของอยู่แล้ว" ซึ่งทางแก้
 *   อยู่ในมือผู้ดูแลทันที (ตั้งชื่ออื่น / ใช้รหัสอื่น หรือใช้ของที่มีอยู่)
 * - `DEPARTMENT_NOT_FOUND` / `PLANT_NOT_FOUND` — `422` ของการผูกพนักงานเข้ากับแผนก/
 *   โรงงาน และ **คนละความหมายกับ `404` ของเส้นทางเดียวกัน**: `422` แปลว่าสิ่งที่อยู่
 *   ใน *body* ไม่มีอยู่ (แผนก/โรงงาน) ส่วน `404` แปลว่า **พนักงานใน path** ไม่มีอยู่
 *   ผู้ดูแลที่อ่านผิดฝั่งจะไปแก้ช่องที่ไม่ได้เสีย
 *
 * `403` ของกลุ่มนี้ **ไม่ใช่** "โรงงานนอกขอบเขต" อย่างหน้าจอธุรกิจ แต่คือ
 * "บัญชีนี้ไม่ใช่ผู้ดูแลระบบ" — หลัง D-060 มีเพียง system role `ADMIN` ที่ถือ
 * `RBAC_ADMIN` ได้เลย ส่วน `404` แปลว่าอะไรขึ้นกับว่าคำขอไหนล้ม: แผนกที่ไม่มีอยู่
 * สำหรับตารางสิทธิ์ และ **พนักงานที่ไม่มีอยู่** สำหรับการมอบ/ถอดบทบาทและการผูก
 * แผนก/โรงงาน
 */

import { StatusBadge } from '../StatusBadge';
import {
  ruleFromFailure,
  type ApiErrorEnvelope,
  type RbacAdminFailure,
} from '../../lib/rbacAdmin/client';
import {
  ADMIN_ROLE_CODE,
  DEPARTMENT_NAME_TAKEN,
  DEPARTMENT_NOT_FOUND,
  EMPLOYEE_ID_TAKEN,
  LAST_ADMIN_CANNOT_BE_REMOVED,
  MODULE_NOT_FOUND,
  PLANT_NOT_FOUND,
  ROLE_CODE_NOT_RECOGNISED,
  SYSTEM_ADMIN_MODULE_NOT_GRANTABLE,
  SYSTEM_ROLE_CODES,
} from '../../lib/rbacAdmin/types';

export type RbacAdminFailureContext =
  /** อ่านรายการแผนก */
  | 'departments'
  /** อ่าน matrix ของแผนกที่เลือก */
  | 'matrix'
  /** บันทึกสิทธิ์ของหนึ่ง (แผนก, module) */
  | 'save'
  /** อ่านรายการบทบาทระบบทั้งองค์กร */
  | 'roles'
  /** มอบ/ถอดบทบาทระบบของพนักงานหนึ่งคน */
  | 'grant'
  /** อ่านบันทึกการเปลี่ยนสิทธิ์ */
  | 'audit-log'
  /** อ่านรายชื่อพนักงานทั้งองค์กร */
  | 'employees'
  /** สร้างแผนกใหม่ */
  | 'create-department'
  /** สร้าง employee master ใหม่ */
  | 'create-employee'
  /** ผูก/ถอนแผนกของพนักงานหนึ่งคน */
  | 'assign-department'
  /** ให้/ถอนสิทธิ์เข้าถึงโรงงานของพนักงานหนึ่งคน */
  | 'assign-plant';

/**
 * ประโยค "ไม่มีอะไรเปลี่ยน" ของแต่ละ context ที่ **เขียน** — สตริงว่างสำหรับ context
 * ที่อ่านอย่างเดียว
 *
 * ทุก endpoint ที่เขียนในกลุ่มนี้เขียน `rbac_audit_log` **ในธุรกรรมเดียวกับการ
 * เปลี่ยนข้อมูล** ล้มจุดใดก็ไม่เหลือทั้งสองอย่าง — ประโยคนี้จึงเป็นข้อเท็จจริงของ
 * contract ไม่ใช่คำปลอบใจ
 *
 * แยกข้อความรายกรณีแทนที่จะใช้ประโยคเดียวทั้งหมด เพราะ "ไม่มีสิทธิ์ใดถูกเปลี่ยน"
 * ไม่ใช่คำตอบของผู้ดูแลที่เพิ่งกดสร้างพนักงาน — เขาอยากรู้ว่า **พนักงานคนนั้นถูก
 * สร้างไปแล้วหรือยัง** ก่อนจะกดซ้ำ
 */
function nothingChangedText(context: RbacAdminFailureContext): string {
  switch (context) {
    case 'save':
    case 'grant':
      return ' ไม่มีสิทธิ์ใดถูกเปลี่ยนและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน';
    case 'create-department':
      return ' ไม่มีแผนกใดถูกสร้างและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน';
    case 'create-employee':
      return ' ไม่มีพนักงานใดถูกสร้างและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน';
    case 'assign-department':
      return ' การสังกัดแผนกยังเป็นชุดเดิมทุกแถวและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน';
    case 'assign-plant':
      return ' สิทธิ์เข้าถึงโรงงานยังเป็นชุดเดิมทุกแถวและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน';
    case 'departments':
    case 'matrix':
    case 'roles':
    case 'audit-log':
    case 'employees':
      return '';
  }
}

function isWriteContext(context: RbacAdminFailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * context ที่ `404` หมายถึง **พนักงานใน path/body** ไม่ใช่แผนก
 *
 * `POST /admin/roles` รับรหัสพนักงานเป็น field ส่วนอีกสองเส้นรับเป็น path segment
 * — ต่างกันที่รูปของคำขอ แต่คำตอบที่ผู้ดูแลต้องอ่านเหมือนกันทุกประการ
 */
function isEmployeeAddressedContext(context: RbacAdminFailureContext): boolean {
  return (
    context === 'grant' || context === 'assign-department' || context === 'assign-plant'
  );
}

export interface RbacAdminFailureNoticeProps {
  readonly failure: RbacAdminFailure;
  readonly context: RbacAdminFailureContext;
  /** ชื่อแผนกที่คำขอนี้อ้างถึง — `404` อ่านรู้เรื่องขึ้นมากเมื่อบอกว่าแผนกไหน */
  readonly departmentName?: string;
  /**
   * รหัสพนักงานที่คำขอนี้อ้างถึง — ด้วยเหตุผลเดียวกับ `departmentName`
   *
   * บนหน้าจอบทบาทระบบ รหัสนี้ถูก **พิมพ์เข้ามาเอง** (เป็น business key ตาม D-039
   * ไม่ใช่ตัวเลือกจากรายการ) `404` ที่ไม่เอ่ยรหัสจึงทิ้งให้ผู้ดูแลเดาว่าพิมพ์ผิด
   * ตรงไหน
   */
  readonly employeeId?: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: RbacAdminFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * ⚠️ ห้ามเพิ่มรหัสที่ contract ไม่ได้ระบุลงใน `switch` นี้ — รหัสที่หน้าจอคิดขึ้นเอง
 * คือข้อความที่ไม่มีวันแสดง และเป็นคำสัญญาที่ backend ไม่เคยให้ไว้
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case LAST_ADMIN_CANNOT_BE_REMOVED:
      return {
        heading: 'ถอดบทบาทนี้ไม่ได้ — เป็นผู้ดูแลระบบคนสุดท้ายที่ยังใช้งานอยู่',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LAST_ADMIN_CANNOT_BE_REMOVED} — ระบบที่ไม่เหลือ ${ADMIN_ROLE_CODE} เลยคือระบบที่ไม่มีใครแก้สิทธิ์ได้อีก และไม่มีทางแต่งตั้งผู้ดูแลคนใหม่ผ่านหน้าจอนี้ได้ ทางแก้คือ **มอบ ${ADMIN_ROLE_CODE} ให้พนักงานอีกคนก่อน** แล้วจึงถอดของคนนี้ (นับเฉพาะพนักงานที่สถานะ ACTIVE — ผู้ดูแลที่ถูกปิดการใช้งานแล้วปลดล็อกใครไม่ได้ จึงไม่นับเป็นคนสุดท้าย) **บทบาทยังอยู่ครบตามเดิมและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน**`,
      };
    case ROLE_CODE_NOT_RECOGNISED:
      return {
        heading: 'เซิร์ฟเวอร์ไม่รู้จักรหัสบทบาทนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${ROLE_CODE_NOT_RECOGNISED} — บทบาทระบบที่ API นี้มอบได้มีเฉพาะ ${SYSTEM_ROLE_CODES.join('/')} การที่คำขอไปถึงเซิร์ฟเวอร์ด้วยรหัสอื่นแปลว่าตัวเลือกบนหน้าจอกับชุดที่เซิร์ฟเวอร์รับไม่ตรงกันแล้ว — แจ้งผู้ดูแลระบบ **ไม่มีบทบาทใดถูกเปลี่ยน**`,
      };
    case SYSTEM_ADMIN_MODULE_NOT_GRANTABLE:
      return {
        heading: 'หน้าจอกับเซิร์ฟเวอร์ไม่ตรงกัน — โมดูลกลุ่มระบบถูกส่งไปทั้งที่ต้องมอบไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SYSTEM_ADMIN_MODULE_NOT_GRANTABLE} — กลุ่ม System Administration มอบผ่านตารางสิทธิ์ไม่ได้เลย สิทธิ์ของมันมาจาก system role ADMIN อย่างเดียว (D-060) หน้าจอนี้ปิดช่องติ๊กของโมดูลกลุ่มนั้นไว้อยู่แล้ว การที่คำขอยังไปถึงเซิร์ฟเวอร์จึงแปลว่าแคตาล็อกฝั่งเซิร์ฟเวอร์กับธงที่ส่งมาให้หน้าจอไม่ตรงกัน — แจ้งผู้ดูแลระบบ อย่าลองกดซ้ำ **ไม่มีสิทธิ์ใดถูกเปลี่ยนและไม่มีบันทึกการเปลี่ยนสิทธิ์ถูกเขียน**`,
      };
    case MODULE_NOT_FOUND:
      return {
        heading: 'ไม่มีโมดูลนี้อยู่ในแคตาล็อกของเซิร์ฟเวอร์แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${MODULE_NOT_FOUND} — รหัสโมดูลที่ส่งไปไม่อยู่ในแคตาล็อก แปลว่าตารางที่เปิดค้างอยู่เก่ากว่าของจริง ให้โหลดตารางใหม่แล้วตั้งค่าอีกครั้ง **ไม่มีสิทธิ์ใดถูกเปลี่ยน**`,
      };
    case DEPARTMENT_NAME_TAKEN:
      return {
        heading: 'ชื่อแผนกนี้มีอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${DEPARTMENT_NAME_TAKEN} — ชื่อแผนกต้องไม่ซ้ำกันทั้งองค์กร ตั้งชื่ออื่น หรือถ้าตั้งใจจะใช้แผนกที่มีอยู่แล้ว ให้เลือกจากรายการแผนกด้านล่างแทนการสร้างใหม่ **ไม่มีแผนกใดถูกสร้างขึ้น และแผนกเดิมที่ชื่อนี้ไม่ถูกแตะต้อง**`,
      };
    case EMPLOYEE_ID_TAKEN:
      return {
        heading: 'รหัสพนักงานนี้มีเจ้าของอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${EMPLOYEE_ID_TAKEN} — รหัสพนักงานเป็นรหัสที่องค์กรกำหนดเองและต้องไม่ซ้ำกัน (D-039) ใช้รหัสอื่น หรือถ้าคนที่ตั้งใจจะเพิ่มคือคนที่ถือรหัสนี้อยู่แล้ว เขามีอยู่ในระบบแล้วและแก้ไขได้จากรายการพนักงานด้านล่าง **ไม่มีพนักงานใดถูกสร้างขึ้น และข้อมูลของคนที่ถือรหัสนี้ไม่ถูกทับ**`,
      };
    case DEPARTMENT_NOT_FOUND:
      return {
        heading: 'ไม่มีแผนกที่คำขอนี้อ้างถึงอยู่แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DEPARTMENT_NOT_FOUND} — แผนกที่เลือกไว้ไม่มีอยู่ในระบบแล้ว (อาจถูกผู้ดูแลอีกคนแก้ระหว่างที่หน้าจอนี้เปิดค้างอยู่) โหลดรายการแผนกใหม่แล้วเลือกอีกครั้ง **การสังกัดแผนกของพนักงานคนนี้ยังเป็นชุดเดิมทุกแถว**`,
      };
    case PLANT_NOT_FOUND:
      return {
        heading: 'ไม่มีโรงงานรหัสนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PLANT_NOT_FOUND} — รหัสโรงงานเป็นรหัสทางธุรกิจ (เช่น BKK-01) ไม่ใช่รหัสที่ระบบสร้าง จึงต้องพิมพ์ให้ตรงกับที่ลงทะเบียนไว้ ตรวจรหัสอีกครั้ง **สิทธิ์เข้าถึงโรงงานของพนักงานคนนี้ยังเป็นชุดเดิมทุกแถว และขอบเขตที่เขามองเห็นไม่เปลี่ยน (D-004)**`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: RbacAdminFailure,
  context: RbacAdminFailureContext,
  departmentName: string | undefined,
  employeeId: string | undefined,
): FailureCopy {
  const department =
    departmentName === undefined || departmentName === '' ? 'แผนกที่เลือกอยู่' : departmentName;
  const employee =
    employeeId === undefined || employeeId === '' ? 'พนักงานที่ระบุ' : employeeId;
  const writing = isWriteContext(context);
  // การเขียนหนึ่งครั้งเป็นธุรกรรมเดียวกับการเขียน `rbac_audit_log` — ล้มจุดใดก็ไม่
  // เหลือทั้งการเปลี่ยนสิทธิ์และแถว audit ประโยคนี้จึงต้องอยู่กับทุก context ที่เขียน
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading: 'บัญชีนี้ไม่ใช่ผู้ดูแลระบบ',
        text: `เซิร์ฟเวอร์ตอบ 403 — ทุกคำขอของหน้าจอนี้ต้องการโมดูล RBAC_ADMIN และหลัง D-060 มีเพียง system role ADMIN เท่านั้นที่ถือมันได้ ไม่ใช่เรื่องของแผนกหรือโรงงาน — ขอสิทธิ์ ADMIN กับผู้ดูแลระบบคนปัจจุบัน${nothingChanged}`,
      };
    case 'not-found':
      // `404` ของกลุ่มนี้อ้างถึงคนละสิ่งกันตามคำขอที่ล้ม — ข้อความเดียวที่ใช้ทั้ง
      // สองกรณีจะส่งผู้ดูแลไปโหลดรายการแผนกใหม่ ทั้งที่สิ่งที่เขาพิมพ์ผิดคือรหัสพนักงาน
      return isEmployeeAddressedContext(context)
        ? {
            heading: `ไม่พบพนักงานรหัส ${employee}`,
            text: `เซิร์ฟเวอร์ตอบ 404 — ไม่มีพนักงานรหัสนี้อยู่ในระบบ รหัสพนักงานเป็นรหัสที่องค์กรกำหนดเอง (D-039) ไม่ใช่ตัวเลขที่ระบบสร้าง จึงพิมพ์ผิดได้ง่าย — ตรวจรหัสอีกครั้ง หรือสร้างพนักงานคนนี้ก่อนแล้วจึงมอบบทบาทให้${nothingChanged}`,
          }
        : {
            heading: `ไม่พบแผนก ${department}`,
            text: `เซิร์ฟเวอร์ตอบ 404 — แผนกนี้ไม่มีอยู่แล้ว (อาจถูกแก้หรือถูกลบโดยผู้ดูแลอีกคนระหว่างที่หน้าจอนี้เปิดค้างอยู่) โหลดรายการแผนกใหม่แล้วเลือกอีกครั้ง${nothingChanged}`,
          };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: `เซิร์ฟเวอร์ตอบ 409 — ชื่อหรือรหัสที่ส่งไปมีเจ้าของอยู่แล้ว ไม่ใช่ข้อผิดพลาดของระบบและการกดซ้ำจะได้คำตอบเดิม${nothingChanged}`,
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น ไม่ได้ระบุแผนก หรือมีฟิลด์ที่ endpoint ไม่รับติดไปด้วย)${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และ **ไม่มีอะไรยืนยันว่าสิทธิ์ถูกเปลี่ยนหรือไม่** — โหลดข้อมูลใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดข้อมูลใหม่เพื่อดูสถานะที่เก็บอยู่จริงก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง และจะไม่เดาขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text:
          context === 'audit-log'
            ? 'หน้าจอปฏิเสธที่จะแสดงบันทึกการเปลี่ยนสิทธิ์ที่อ่านได้ไม่ครบ — ร่องรอยที่เติมช่องว่างด้วยค่าที่เดาขึ้นมาไม่ใช่หลักฐาน และนี่คือสมุดที่ผู้ตรวจสอบใช้ตอบว่าใครเปลี่ยนสิทธิ์ของใคร'
            : 'หน้าจอปฏิเสธที่จะแสดงข้อมูลสิทธิ์ที่อ่านได้ไม่ครบ ค่าที่ถูกเดาไว้คือค่าที่ผู้ดูแลจะกดบันทึกทับ แล้วกลายเป็นการถอนสิทธิ์ที่ไม่มีใครตั้งใจ',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function RbacAdminFailureNotice({
  failure,
  context,
  departmentName,
  employeeId,
  onRetry,
}: RbacAdminFailureNoticeProps) {
  const copy = failureCopy(failure, context, departmentName, employeeId);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  // `409` และ `422` เป็นคำตอบที่ **ผู้ดูแลแก้เองได้** (ตั้งชื่ออื่น, เลือกแผนกที่มีจริง)
  // ต่างจากความล้มเหลวที่เหลือซึ่งเขาทำอะไรไม่ได้เลย — สีของป้ายจึงบอกเรื่องนั้น
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid="rbac-admin-failure"
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="rbac-admin-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="rbac-admin-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
